import React from 'react';

// antd's Table has no column resizing of its own, so this replaces its header
// cell (via `components.header.cell`) with one that carries a drag handle on
// its right edge. The column's `onHeaderCell` passes `onResize(width)` while
// dragging and `onResizeEnd(width)` on release (`null` on a double-click,
// meaning "back to the default width"); a cell without them (the selection
// column) renders as a plain <th>.
//
// Pointer events rather than mouse events, so a touch screen can drag too.
// The move/up listeners go on the document, not the handle, so the drag keeps
// tracking when the pointer outruns the 6px handle.
const MIN_WIDTH = 60;

function ResizableHeaderCell({ onResize, onResizeEnd, ...rest }) {
    if (!onResize) return <th {...rest} />;

    const startDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const th = e.currentTarget.parentElement;
        const startX = e.clientX;
        const startWidth = th.getBoundingClientRect().width;
        let width = startWidth;
        const move = (ev) => {
            width = Math.max(MIN_WIDTH, Math.round(startWidth + ev.clientX - startX));
            onResize(width);
        };
        const up = () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            document.body.classList.remove('browse-col-resizing');
            // Releasing inside the header fires a click on the <th>, which
            // antd reads as "toggle sort". Swallow that one click, and only
            // that one: the timeout drops the guard if no click follows (a
            // release outside the table), so it can't eat a later real click.
            const swallow = ev => ev.stopPropagation();
            window.addEventListener('click', swallow, { capture: true, once: true });
            setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
            if (width !== startWidth) onResizeEnd?.(width);
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        // Keeps the resize cursor and blocks text selection for the whole
        // drag, even while the pointer is over the table body.
        document.body.classList.add('browse-col-resizing');
    };

    return (
        <th {...rest} style={{ ...rest.style, position: 'relative' }}>
            {rest.children}
            <span
                className="browse-col-resize-handle"
                onPointerDown={startDrag}
                onClick={e => e.stopPropagation()}
                // Back to the column's default width.
                onDoubleClick={e => { e.stopPropagation(); onResizeEnd?.(null); }}
                aria-hidden="true"
            />
        </th>
    );
}

export default ResizableHeaderCell;
